/**
 * Comptage d'un produit — « mini-inventaire individuel ».
 *
 * Un seul produit, tous ses lots actifs : l'opérateur tape ce qu'il compte
 * réellement (réserve et rayon), l'écart s'affiche, un motif est exigé dès
 * qu'un écart existe, et chaque lot modifié part dans `POST /lots/:id/adjust`
 * (mouvements ADJUSTMENT tracés, fiche resynchronisée).
 *
 * Né du cas Nectarine Blanche (30/08/2026) : l'édition directe du stock sur la
 * fiche produit est ignorée par le backend dès qu'un lot actif existe (garde
 * anti « Bug Mojito »), et ouvrir une session d'inventaire pour UN produit est
 * disproportionné. Ce comptage ne marque PAS le rayon comme inventorié — la
 * couverture réglementaire du 31/12 passe toujours par les sessions.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { posApi } from '../api/pos.api';
import { stockOutApi } from '../api/stock-out.api';
import WebcamCaptureModal from './ui/WebcamCaptureModal';
import { usePOSStore } from '../hooks/usePOSStore';
import { formatQty } from '../utils/pos.utils';
import type { ProductLot } from '../types/lots.types';
import { logger } from '../utils/logger';
import { offlineDb } from '../lib/offline/OfflineDatabase';

/** Mêmes motifs que l'inventaire — l'analyse de la démarque lit les deux. */
const MOTIFS = [
  { value: 'BREAKAGE', label: 'Casse' },
  { value: 'EXPIRY', label: 'Péremption' },
  { value: 'THEFT', label: 'Vol / démarque inconnue' },
  { value: 'RECEPTION_ERROR', label: 'Erreur de réception' },
  { value: 'ENTRY_ERROR', label: 'Erreur de saisie' },
  { value: 'FIELD_ADJUSTMENT', label: 'Ajustement production (champ)' },
  { value: 'OTHER', label: 'Autre' },
] as const;

interface CountProduct {
  id: string;
  name: string;
  unit?: string | null;
  is_weighted?: boolean | null;
}

/** Saisie tolérante à la virgule (clavier belge / iOS). */
const parseQty = (raw: string): number | null => {
  const v = raw.trim().replace(',', '.');
  if (v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const fmtInput = (v: number) => String(parseFloat(Number(v).toFixed(3)));

export default function ProductCountModal({
  product,
  onClose,
  onSaved,
}: {
  product: CountProduct;
  onClose: () => void;
  /** Appelé après enregistrement réussi — recharger lots / listes chez l'appelant. */
  onSaved: () => void;
}) {
  const [lots, setLots] = useState<ProductLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Valeurs saisies, indexées par lot : pré-remplies avec le théorique — un lot
  // non touché ne produit AUCUN ajustement.
  const [backroomInputs, setBackroomInputs] = useState<Record<string, string>>({});
  const [displayInputs, setDisplayInputs] = useState<Record<string, string>>({});
  const [motif, setMotif] = useState('');
  /** Bloc du motif — pour y amener l'opérateur quand il manque. */
  const motifRef = useRef<HTMLDivElement>(null);
  const [note, setNote] = useState('');
  // Photo de preuve (article gâté, casse…) — facultative, attachée aux mouvements.
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  // Derniers ajustements du produit (avec leurs photos) — le comptage précédent
  // se relit ici, c'est aussi là que les photos restent consultables.
  const [history, setHistory] = useState<any[]>([]);
  // Ventes de CE produit encaissées sur cette caisse mais pas encore enregistrées
  // au serveur (file hors ligne, conflit de stock…). Compter maintenant les
  // ferait déduire deux fois : le comptage exclut déjà ce que le client a
  // emporté, et la vente sera décomptée en plus à son enregistrement.
  const [ventesEnAttente, setVentesEnAttente] = useState(0);

  useEffect(() => {
    let annule = false;
    offlineDb.pendingSales
      .filter(
        (v) =>
          v.status !== 'synced' &&
          v.status !== 'abandoned' &&
          (v.lines || []).some((l) => l.product_id === product.id && !l.is_return),
      )
      .count()
      .then((n) => { if (!annule) setVentesEnAttente(n); })
      .catch(() => { /* base locale absente (hors caisse) : rien à signaler */ });
    return () => { annule = true; };
  }, [product.id]);

  // Lignes de comptage déjà annulées (leur écriture inverse porte leur id).
  const [annules, setAnnules] = useState<Set<string>>(new Set());
  const [annulation, setAnnulation] = useState<string | null>(null);
  const [rechargement, setRechargement] = useState(0);

  const annulerLigne = async (m: any) => {
    if (!window.confirm(
      `Annuler ce comptage du ${new Date(m.created_at).toLocaleDateString('fr-BE')} ?\n\n` +
      `${Number(m.quantity) > 0 ? '+' : ''}${formatQty(Number(m.quantity))} sur ce lot sera remis comme avant, ` +
      `et la perte sortira du rapport de démarque. Vous pourrez ensuite recompter.`,
    )) return;
    setAnnulation(m.id);
    setError(null);
    try {
      await posApi.annulerComptage(m.id, usePOSStore.getState().vendorId || undefined);
      setRechargement((n) => n + 1);
    } catch (e: any) {
      setError(e?.message || 'Annulation impossible.');
    } finally {
      setAnnulation(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        // GET /lots/product/:id filtre DÉJÀ status ACTIVE côté serveur, et sa
        // réponse n'inclut PAS le champ status — filtrer dessus ici éliminait
        // tous les lots (« aucun lot actif » sur un produit plein, 01/09/2026).
        const all: ProductLot[] = await posApi.getProductLots(product.id);
        const actifs = all || [];
        setLots(actifs);
        const br: Record<string, string> = {};
        const di: Record<string, string> = {};
        for (const l of actifs) {
          br[l.id] = fmtInput(l.stock_backroom);
          di[l.id] = fmtInput(l.stock_display);
        }
        setBackroomInputs(br);
        setDisplayInputs(di);
      } catch (e) {
        logger.error('[ProductCount] chargement lots échoué:', e);
        setError('Impossible de charger les lots de ce produit.');
      } finally {
        setLoading(false);
      }
      try {
        const mv: any[] = await posApi.getProductMovements(product.id);
        setAnnules(new Set((mv || []).filter((m) => m.reference_type === 'comptage_annule').map((m) => String(m.reference_id))));
        setHistory((mv || []).filter((m) => m.type === 'ADJUSTMENT').slice(0, 5));
      } catch { /* l'historique est un confort, jamais bloquant */ }
    })();
  }, [product.id, rechargement]);

  const uploadPhoto = async (file: File) => {
    try {
      setUploadingPhoto(true);
      setError(null);
      setPhotoUrl(await stockOutApi.uploadPhoto(file));
    } catch (e) {
      logger.error('[ProductCount] upload photo échoué:', e);
      setError("Échec de l'envoi de la photo — le comptage reste possible sans elle.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const pickPhotoFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    // L'input DOIT etre pose dans la page : detache, WebKit peut le liberer
    // pendant que la camera est ouverte (plusieurs dizaines de secondes) et
    // `onchange` ne part jamais — la photo est prise, puis perdue en silence.
    // Invisible et hors flux pour ne rien deplacer a l'ecran.
    input.style.position = 'fixed';
    input.style.left = '-9999px';
    input.onchange = (e) => {
      const f = (e.target as HTMLInputElement).files?.[0];
      if (f) void uploadPhoto(f);
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  };

  // iOS (iPhone/iPad, y compris iPadOS qui se présente en MacIntel tactile) :
  // getUserMedia dans une PWA installée RECHARGE l'app à l'octroi de la permission
  // caméra → tout le comptage en cours est perdu (« clôture la démarque sans la
  // compter », 01/09/2026). On y prend la photo par la caméra NATIVE (<input
  // capture>), qui ne recharge pas. Le modal getUserMedia reste pour le Mac mini.
  const isIOS =
    typeof navigator !== 'undefined' &&
    (/iP(hone|od|ad)/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1));

  // Nature « au poids » en OU (jamais `??` : is_weighted vaut false par défaut).
  const isWeight = (lot: ProductLot) =>
    !!(lot.is_weighted || product.is_weighted || lot.unit === 'kg' || product.unit === 'kg');

  /** Lots dont la saisie, valide, diffère du théorique → à ajuster. */
  const changes = useMemo(() => {
    return lots
      .map((lot) => {
        const br = parseQty(backroomInputs[lot.id] ?? '');
        const di = parseQty(displayInputs[lot.id] ?? '');
        if (br === null || di === null) return null; // saisie incomplète/invalide → ignoré
        const diffBr = br - Number(lot.stock_backroom);
        const diffDi = di - Number(lot.stock_display);
        if (Math.abs(diffBr) < 0.0005 && Math.abs(diffDi) < 0.0005) return null;
        return { lot, br, di, diff: diffBr + diffDi };
      })
      .filter(Boolean) as Array<{ lot: ProductLot; br: number; di: number; diff: number }>;
  }, [lots, backroomInputs, displayInputs]);

  const invalids = useMemo(
    () =>
      lots.filter(
        (lot) =>
          parseQty(backroomInputs[lot.id] ?? '') === null ||
          parseQty(displayInputs[lot.id] ?? '') === null,
      ),
    [lots, backroomInputs, displayInputs],
  );

  const totalDiff = changes.reduce((s, c) => s + c.diff, 0);

  const save = async () => {
    if (changes.length === 0) return;
    if (!motif) {
      setError('Choisis un motif : il explique l\'écart dans l\'historique des mouvements.');
      // Le bloc du motif est dans la zone défilante : sans ce défilement,
      // l'opérateur lit « choisis un motif » sans voir où le choisir.
      motifRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSaving(true);
    setError(null);
    const motifLabel = MOTIFS.find((m) => m.value === motif)?.label || motif;
    // stock_movements.created_by est un UUID de VENDEUR (nullable) — jamais un
    // nom : « Philippe » y provoquait un 500 Prisma (01/09/2026). Hors contexte
    // vendeur (dashboard), on n'envoie rien.
    const createdBy = usePOSStore.getState().vendorId || undefined;
    try {
      // Séquentiel volontairement : chaque ajustement verrouille le lot et
      // resynchronise la fiche ; en parallèle ils se disputeraient la ligne produit.
      for (const c of changes) {
        await posApi.adjustLotStock(c.lot.id, {
          actual_backroom: c.br,
          actual_display: c.di,
          reason: `Comptage produit — ${motifLabel}`,
          notes: note.trim() || undefined,
          photo_url: photoUrl || undefined,
          created_by: createdBy,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      logger.error('[ProductCount] ajustement échoué:', e);
      setError(e instanceof Error ? e.message : 'Échec de l\'ajustement — rien d\'autre n\'a été modifié.');
      setSaving(false);
    }
  };

  const unit = product.unit || 'pièce';

  // Rendu HORS de l'arbre DOM du parent. ProductEditModal enveloppe toute sa
  // fiche dans <form onSubmit={handleSubmit}> et monte ce modal DEDANS : sans
  // portail, chaque bouton d'ici valait « submit » et un « Entree » dans un
  // champ de comptage envoyait la fiche produit. Le 16/09/2026, cliquer
  // « Prendre une photo » fermait donc tout en annoncant un enregistrement,
  // et la photo n'etait jamais jointe (Tomate Grappe). Le portail ferme la
  // classe entiere, y compris pour ce qu'on ajoutera ici demain.
  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-start justify-center p-4 pt-10">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[85vh] flex flex-col shadow-2xl">
        <div className="p-4 border-b border-gray-200 flex items-center gap-3 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">🔢 Compter « {product.name} »</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Saisis ce que tu comptes réellement — un lot non modifié n'est pas touché.
            </p>
          </div>
          <button type="button" onClick={onClose} className="ml-auto text-gray-400 text-3xl leading-none px-2">
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {ventesEnAttente > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 border-2 border-amber-300 text-amber-900 text-sm">
              <p className="font-bold">
                ⚠️ {ventesEnAttente === 1 ? 'Une vente' : `${ventesEnAttente} ventes`} de ce produit{' '}
                {ventesEnAttente === 1 ? "n'est" : "ne sont"} pas encore {ventesEnAttente === 1 ? 'enregistrée' : 'enregistrées'} sur cette caisse.
              </p>
              <p className="mt-1">
                Attendez qu'elle{ventesEnAttente > 1 ? 's' : ''} passe{ventesEnAttente > 1 ? 'nt' : ''} (indicateur de la caisse) avant
                de compter : sinon elle{ventesEnAttente > 1 ? 's' : ''} ser{ventesEnAttente > 1 ? 'ont' : 'a'} déduite{ventesEnAttente > 1 ? 's' : ''} deux
                fois. Pour une récolte, encodez plutôt la réception.
              </p>
            </div>
          )}
          {loading && <p className="text-center text-gray-500 py-6">Chargement des lots…</p>}
          {!loading && lots.length === 0 && (
            <p className="text-center text-gray-500 py-6">
              Aucun lot actif — rien à compter. (Un produit sans lot se gère à la réception.)
            </p>
          )}

          {lots.map((lot) => {
            const br = parseQty(backroomInputs[lot.id] ?? '');
            const di = parseQty(displayInputs[lot.id] ?? '');
            const diff =
              br !== null && di !== null
                ? br + di - (Number(lot.stock_backroom) + Number(lot.stock_display))
                : null;
            return (
              <div key={lot.id} className="rounded-xl border-2 border-gray-200 p-3">
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="font-bold text-gray-900">Lot {lot.lot_number}</span>
                  {lot.expiry_date && (
                    <span className="text-orange-700">
                      DLC {new Date(lot.expiry_date).toLocaleDateString('fr-BE')}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-gray-500">
                    Théorique : R {formatQty(lot.stock_backroom)} · Rayon {formatQty(lot.stock_display)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <label className="block">
                    <span className="text-xs text-gray-600">Réserve comptée ({unit})</span>
                    <input
                      type="text"
                      inputMode={isWeight(lot) ? 'decimal' : 'numeric'}
                      value={backroomInputs[lot.id] ?? ''}
                      onChange={(e) =>
                        setBackroomInputs((p) => ({ ...p, [lot.id]: e.target.value }))
                      }
                      className="mt-1 w-full px-3 py-2 border-2 border-orange-200 rounded-lg text-lg font-semibold text-center focus:outline-none focus:border-orange-400"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-gray-600">Rayon compté ({unit})</span>
                    <input
                      type="text"
                      inputMode={isWeight(lot) ? 'decimal' : 'numeric'}
                      value={displayInputs[lot.id] ?? ''}
                      onChange={(e) =>
                        setDisplayInputs((p) => ({ ...p, [lot.id]: e.target.value }))
                      }
                      className="mt-1 w-full px-3 py-2 border-2 border-green-200 rounded-lg text-lg font-semibold text-center focus:outline-none focus:border-green-400"
                    />
                  </label>
                </div>
                {diff !== null && Math.abs(diff) >= 0.0005 && (
                  <p className={`mt-2 text-sm font-semibold ${diff < 0 ? 'text-red-700' : 'text-blue-700'}`}>
                    Écart : {diff > 0 ? '+' : ''}{formatQty(diff)} {unit}
                  </p>
                )}
              </div>
            );
          })}

          {changes.length > 0 && (
            <div ref={motifRef} className="rounded-xl bg-amber-50 border-2 border-amber-200 p-3 space-y-2">
              <p className="text-sm font-semibold text-amber-900">
                Écart total : {totalDiff > 0 ? '+' : ''}{formatQty(totalDiff)} {unit} sur {changes.length} lot{changes.length > 1 ? 's' : ''}
              </p>
              <select
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                className="w-full px-3 py-2 border-2 border-amber-300 rounded-lg bg-white"
              >
                <option value="">Motif de l'écart (obligatoire)…</option>
                {MOTIFS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Remarque (facultatif)"
                className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm"
              />
              <div className="flex items-center gap-2">
                {photoUrl ? (
                  <>
                    <a href={photoUrl} target="_blank" rel="noopener noreferrer">
                      <img src={photoUrl} alt="Preuve" className="h-14 w-14 object-cover rounded-lg border border-amber-300" />
                    </a>
                    <button type="button" onClick={() => setPhotoUrl(null)} className="text-xs text-red-600 underline">
                      Retirer
                    </button>
                  </>
                ) : (
                  <>
                    {/* iOS : caméra native (getUserMedia recharge la PWA et perd le
                        comptage). Mac mini : webcam getUserMedia + fichier. */}
                    {isIOS ? (
                      <button type="button"
                        onClick={pickPhotoFile}
                        disabled={uploadingPhoto}
                        className="px-3 py-1.5 text-sm border border-amber-400 text-amber-800 rounded-lg disabled:opacity-40"
                      >
                        {uploadingPhoto ? 'Envoi…' : '📸 Prendre une photo'}
                      </button>
                    ) : (
                      <>
                        <button type="button"
                          onClick={() => setShowWebcam(true)}
                          disabled={uploadingPhoto}
                          className="px-3 py-1.5 text-sm border border-amber-400 text-amber-800 rounded-lg disabled:opacity-40"
                        >
                          📸 Webcam
                        </button>
                        <button type="button"
                          onClick={pickPhotoFile}
                          disabled={uploadingPhoto}
                          className="px-3 py-1.5 text-sm border border-amber-400 text-amber-800 rounded-lg disabled:opacity-40"
                        >
                          {uploadingPhoto ? 'Envoi…' : '🖼️ Photo / fichier'}
                        </button>
                      </>
                    )}
                    <span className="text-xs text-amber-700">facultatif — preuve de l'écart</span>
                  </>
                )}
              </div>
            </div>
          )}

          {history.length > 0 && (
            <div className="rounded-xl border border-gray-200 p-3">
              <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
                Derniers ajustements
              </p>
              <div className="space-y-1.5">
                {history.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="shrink-0">{new Date(m.created_at).toLocaleDateString('fr-BE')}</span>
                    <span className={`font-semibold shrink-0 ${Number(m.quantity) < 0 ? 'text-red-700' : 'text-blue-700'}`}>
                      {Number(m.quantity) > 0 ? '+' : ''}{formatQty(Number(m.quantity))}
                    </span>
                    <span className={`truncate ${annules.has(String(m.id)) ? 'line-through text-gray-400' : ''}`}>{m.reason}</span>
                    {/* Annuler un comptage récent : erreur de saisie (raisin rouge
                        compté à zéro, 07/10/2026). 7 jours, une fois. */}
                    {String(m.reason || '').startsWith('Comptage produit') &&
                      !annules.has(String(m.id)) &&
                      Date.now() - new Date(m.created_at).getTime() < 7 * 86_400_000 && (
                        <button
                          type="button"
                          onClick={() => void annulerLigne(m)}
                          disabled={annulation !== null}
                          className="ml-auto shrink-0 rounded-md border border-red-300 px-2 py-1 text-[11px] font-semibold text-red-700 active:bg-red-50 disabled:opacity-40"
                        >
                          {annulation === m.id ? '…' : 'Annuler'}
                        </button>
                      )}
                    {annules.has(String(m.id)) && <span className="ml-auto shrink-0 text-[11px] text-gray-500">annulé</span>}
                    {m.photo_url && (
                      <a href={m.photo_url} target="_blank" rel="noopener noreferrer" className="ml-auto shrink-0" title="Voir la photo">
                        📷
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        <WebcamCaptureModal
          isOpen={showWebcam}
          onClose={() => setShowWebcam(false)}
          onCapture={(f) => void uploadPhoto(f)}
        />

        {/*
          Le message d'erreur vit dans le PIED, pas dans le corps défilant.
          Placé en fin de corps, il tombait sous la ligne de flottaison dès que
          le produit avait plusieurs lots : le clic sans motif était refusé, et
          le refus s'affichait hors écran. L'opérateur voyait un bouton qui
          accepte le clic et ne fait rien (constaté le 02/09/2026 sur la
          pastèque). Un message d'erreur qu'on peut ne pas voir ne sert à rien.
        */}
        <div className="p-4 border-t border-gray-200 shrink-0">
          {error && (
            <p className="text-sm text-red-700 font-medium mb-3" role="alert">
              {error}
            </p>
          )}
          <div className="flex gap-3">
            <button type="button"
              onClick={onClose}
              className="flex-1 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700"
            >
              Annuler
            </button>
            <button type="button"
              onClick={save}
              // Volontairement CLIQUABLE sans motif : son libellé dit ce qui
              // manque, et le clic pose le message dans le pied PUIS amène au
              // champ. Un bouton grisé dirait « non » sans jamais dire où aller.
              disabled={saving || loading || changes.length === 0 || invalids.length > 0}
              className="flex-1 py-3 rounded-xl font-semibold text-white bg-blue-600 disabled:opacity-40"
            >
              {saving
                ? 'Enregistrement…'
                : changes.length === 0
                  ? 'Aucun écart'
                  : !motif
                    ? 'Choisis un motif'
                    : `Ajuster ${changes.length} lot${changes.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
